/* eslint-disable class-methods-use-this */
import { Pagination } from '@packages/toolbox/dist/declarations/pagination';
import { AxiosResponse } from 'axios';
import { getHttp, http } from 'SharedDir/backend/axios/http';
import { cloneDeep } from 'lodash';

import SimpleApi from 'SharedDir/backend/api/classes/simpleApi';
import SERVICE_ROUTES, {
  routesUrl,
} from 'SharedDir/backend/endpoints/administration-service-routes';
import { Status } from 'SharedDir/constants/approval';
import { CopyFormType } from 'SharedDir/declarations/copy';
import { Paginated } from 'SharedDir/declarations/pagination';
import { Section, SectionsArrange, SectionsList } from 'SharedDir/declarations/section-field';
import { NServiceRoute } from 'SharedDir/declarations/service-route';

import { ParamsAllRecords, paramsAllRequest } from '../default-params';

export type Filters = {
  name: string | null;
  isTemplate: number | null;
};

export type ServiceRouteResponseQueryParams = Pagination.PaginationParamsOut<Filters>;

type ServiceRouteResponseCropped = Paginated<NServiceRoute.IServiceRouteShort, {}>;

type ServiceRouteStepsResponse = Pagination.PaginationAndFilterParams<{
  data: NServiceRoute.ServiceRouteStepList;
}>;

type ServiceRouteStepSectionsResponse = Pagination.PaginationParams<{
  data: SectionsList;
}>;

type ServiceRouteStepWithSectionsResponse = {
  id: number;
  name: string;
  steps: NServiceRoute.ServiceRouteStepList;
};

type ServiceRouteStepParamsResponse = {
  id: number;
  params: NServiceRoute.IServiceRouteStepParams;
};

type ServiceRouteTransitionsParamsResponse = {
  id: number;
  params: NServiceRoute.ServiceRouteParams;
};

export type ServiceRouteTransitionsParamsResponseBadRequest = {
  from_step_id: number | null;
  to_step_id: number | null;
  message: number | null;
  transition_id: number | null;
};

export default class AdministrationServiceRouteApiClass extends SimpleApi<
  NServiceRoute.IServiceRouteNonNullable,
  NServiceRoute.IServiceRouteBase,
  NServiceRoute.IServiceRouteBase
> {
  constructor() {
    super(routesUrl);
  }

  /**
   * Получить все записи по мере поддержки/услуги
   *
   * @param paginationParams
   * @returns
   */
  public async getAllServiceRoutesCroppedByService(
    serviceId: number,
    isTemplate: boolean | null = false
  ): Promise<ServiceRouteResponseCropped> {
    const params = {
      ...paramsAllRequest,
      serviceId,
      isTemplate,
    };

    const { data }: AxiosResponse<ServiceRouteResponseCropped> = await http({
      ...SERVICE_ROUTES.ROUTES.LIST_BRIEF,
      params,
    });

    return data;
  }

  /**
   * Проведение валидации маршрута
   *
   * @param id
   * @param params
   * @returns
   */
  public async validateServiceRoute(
    id: number,
    params: NServiceRoute.ServiceRouteParams
  ): Promise<ServiceRouteTransitionsParamsResponse> {
    const endpoint = SERVICE_ROUTES.ROUTES.VALIDATE(id);
    const responseData: ServiceRouteTransitionsParamsResponse = await http({
      ...endpoint,
      data: { params },
    })
      .then((r): ServiceRouteTransitionsParamsResponse => r.data)
      .catch((e) => {
        throw e;
      });

    return responseData;
  }

  /**
   * Опубликовать маршрут меры поддержки
   *
   * @param id
   * @returns
   */
  public async publish(id: number): Promise<NServiceRoute.IServiceRouteNonNullable> {
    const responseData: NServiceRoute.IServiceRouteNonNullable = await http({
      ...SERVICE_ROUTES.ROUTES.PUBLISH(id),
    })
      .then((r): NServiceRoute.IServiceRouteNonNullable => r.data)
      .catch((e) => {
        throw e;
      });

    return responseData;
  }

  /**
   * Смена статуса маршрута меры поддержки
   *
   * @param id
   * @param status
   * @returns
   */
  public async changeStatus(
    id: number,
    status: Status
  ): Promise<NServiceRoute.IServiceRouteNonNullable> {
    const responseData: NServiceRoute.IServiceRouteNonNullable = await http({
      ...SERVICE_ROUTES.ROUTES.CHANGE_STATUS(id),
      params: { status },
    })
      .then((r): NServiceRoute.IServiceRouteNonNullable => r.data)
      .catch((e) => {
        throw e;
      });

    return responseData;
  }

  /**
   * Восстановить маршрут услуги
   * @param id
   * @returns
   */
  public async restoreServiceRoute(id: number): Promise<NServiceRoute.IServiceRouteNonNullable> {
    const endpoint = SERVICE_ROUTES.ROUTES.GET(id);
    const responseData: NServiceRoute.IServiceRouteNonNullable = await http({ ...endpoint })
      .then((r): NServiceRoute.IServiceRouteNonNullable => r.data)
      .catch((e) => {
        throw e;
      });

    return responseData;
  }

  /**
   * Копировать маршрут услуги
   * @param id
   * @returns
   */
  public async copy(data: CopyFormType): Promise<NServiceRoute.IServiceRouteNonNullable> {
    const endpoint = SERVICE_ROUTES.ROUTES.COPY(data.id);
    console.log(data);
    const responseData: NServiceRoute.IServiceRouteNonNullable = await http({
      ...endpoint,
      data,
    })
      .then((r): NServiceRoute.IServiceRouteNonNullable => r.data)
      .catch((e) => {
        throw e;
      });

    return responseData;
  }

  /**
   * Получение всех этапов маршрута
   *
   * @param routeId
   * @returns
   */
  public async getAllStepsByRoute(
    routeId: number,
    pagination: ParamsAllRecords = paramsAllRequest
  ): Promise<ServiceRouteStepsResponse> {
    const { data }: AxiosResponse<ServiceRouteStepsResponse> = await http({
      ...SERVICE_ROUTES.STEPS.LIST,
      params: { ...pagination, routeId },
    });

    return data;
  }

  /**
   * Получение всех этапов маршрута c секциями
   *
   * @param routeId
   * @returns
   */
  public async getAllStepsWithSectionsByRoute(
    routeId: number,
    pagination: ParamsAllRecords = paramsAllRequest
  ): Promise<ServiceRouteStepWithSectionsResponse> {
    const { data }: AxiosResponse<ServiceRouteStepWithSectionsResponse> = await http({
      ...SERVICE_ROUTES.ROUTES.LIST_BRIEF_TREE(routeId),
      params: { ...pagination },
    });

    return data;
  }

  /**
   * Получение определенного этапа маршрута
   *
   * @param routeId
   * @returns
   */
  public async getServiceRouteStep(stepId: number): Promise<NServiceRoute.IServiceRouteStep> {
    const { data }: AxiosResponse<NServiceRoute.IServiceRouteStep> = await http({
      ...SERVICE_ROUTES.STEPS.GET(stepId),
    });

    return data;
  }

  /**
   * Создание этапа маршрута
   *
   * @param data
   * @returns
   */
  public async storeServiceRouteStep(
    data: NServiceRoute.IServiceRouteStep
  ): Promise<NServiceRoute.IServiceRouteStep> {
    const responseData: NServiceRoute.IServiceRouteStep = await http({
      ...SERVICE_ROUTES.STEPS.STORE,
      data,
    })
      .then((r): NServiceRoute.IServiceRouteStep => r.data)
      .catch((e) => {
        throw e;
      });

    return responseData;
  }

  /**
   * Обновление этапа маршрута
   *
   * @param data
   * @returns
   */
  public async updateServiceRouteStep(
    id: number,
    data: NServiceRoute.IServiceRouteStep
  ): Promise<NServiceRoute.IServiceRouteStep> {
    const responseData: NServiceRoute.IServiceRouteStep = await http({
      ...SERVICE_ROUTES.STEPS.UPDATE(id),
      data,
    })
      .then((r): NServiceRoute.IServiceRouteStep => r.data)
      .catch((e) => {
        throw e;
      });

    return responseData;
  }

  /**
   * Обновление параментов этапа маршрута
   *
   * @param data
   * @returns
   */
  public async updateServiceRouteStepParams(
    id: number,
    params: NServiceRoute.IServiceRouteStepParams
  ): Promise<ServiceRouteStepParamsResponse> {
    const responseData: ServiceRouteStepParamsResponse = await http({
      ...SERVICE_ROUTES.STEPS.UPDATE_PARAMS(id),
      data: { params },
    })
      .then((r): ServiceRouteStepParamsResponse => r.data)
      .catch((e) => {
        throw e;
      });

    return responseData;
  }

  /**
   * Удалить этап маршрута услуги
   *
   * @param stepId
   * @returns
   */
  public async deleteServiceRouteStep(stepId: number): Promise<NServiceRoute.IServiceRouteStep> {
    const endpoint = SERVICE_ROUTES.STEPS.DELETE(stepId);
    const responseData: NServiceRoute.IServiceRouteStep = await getHttp()
      .delete(endpoint.url)
      .then((r: AxiosResponse<NServiceRoute.IServiceRouteStep>) => r.data)
      .catch((e) => {
        throw e;
      });

    return responseData;
  }

  /**
   * Изменить сортировку секций этапа
   *
   * @param stepId
   * @param pagination
   * @returns
   */
  public async arrangeServiceRouteStepSections(
    stepId: number,
    dataArrange: Array<SectionsArrange>
  ): Promise<any> {
    const { data }: AxiosResponse<any> = await http({
      ...SERVICE_ROUTES.STEPS.ARRANGE_SECTIONS(stepId),
      data: { sections: dataArrange },
    });

    return data;
  }

  /**
   * Получить все Секции Шага
   *
   * @param stepId
   * @param pagination
   * @returns
   */
  public async getServiceRouteStepSections(
    stepId: number,
    pagination: ParamsAllRecords = paramsAllRequest
  ): Promise<ServiceRouteStepSectionsResponse> {
    const paginationData = cloneDeep(pagination);
    paginationData.sortBy = 'order';

    const { data }: AxiosResponse<ServiceRouteStepSectionsResponse> = await http({
      ...SERVICE_ROUTES.SECTIONS.LIST,
      params: { ...paginationData, stepId },
    });

    return data;
  }

  public async storeServiceRouteStepSection(routeId: number, data: Section): Promise<Section> {
    const responseData: Section = await http({
      ...SERVICE_ROUTES.SECTIONS.STORE,
      data,
    })
      .then((r): Section => r.data)
      .catch((e) => {
        throw e;
      });

    return responseData;
  }

  public async updateServiceRouteStepSection(sectionId: number, data: Section): Promise<Section> {
    const responseData: Section = await http({
      ...SERVICE_ROUTES.SECTIONS.UPDATE(sectionId),
      data,
    })
      .then((r): Section => r.data)
      .catch((e) => {
        throw e;
      });

    return responseData;
  }

  public async deleteServiceRouteStepSection(sectionId: number): Promise<Section> {
    const endpoint = SERVICE_ROUTES.SECTIONS.DELETE(sectionId);
    const responseData: Section = await getHttp()
      .delete(endpoint.url)
      .then((r: AxiosResponse<Section>) => r.data)
      .catch((e) => {
        throw e;
      });

    return responseData;
  }

  /**
   * Создать шаблон для экспорта заявлений
   *
   * @param routeId
   * @param data
   */
  public async createExportTemplate(
    routeId: number,
    data: NServiceRoute.ExportTemplateBare
  ): Promise<NServiceRoute.ExportTemplate> {
    const endpoint = SERVICE_ROUTES.EXPORT_TEMPLATE.STORE(routeId);
    const formData = new FormData();
    formData.append('file', data.file as File);
    formData.append('name', data.name);
    formData.append('signatureRequired', String(data.signatureRequired));

    data.allowedExportFileTypes.forEach((type) => {
      formData.append('allowedExportFileTypes', type);
    });

    const responseData: NServiceRoute.ExportTemplate = await http({
      ...endpoint,
      data: formData,
      params: {
        stepId: data.stepId,
      },
    })
      .then((r): NServiceRoute.ExportTemplate => r.data)
      .catch((e) => {
        throw e;
      });

    return responseData;
  }

  /**
   * Удалить шаблон для экспорта заявлений
   *
   * @param routeId
   * @param data
   */
  public async deleteExportTemplate(
    routeId: number,
    templateId: number
  ): Promise<NServiceRoute.ExportTemplate> {
    const endpoint = SERVICE_ROUTES.EXPORT_TEMPLATE.DELETE(routeId, templateId);

    const responseData: NServiceRoute.ExportTemplate = await http({
      ...endpoint,
    })
      .then((r): NServiceRoute.ExportTemplate => r.data)
      .catch((e) => {
        throw e;
      });

    return responseData;
  }
}
